import React, { FC, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Card, Col, ConfigProvider, Flex, Row, Select, Switch, Typography } from 'antd';
import { Text } from 'theme/typography';
import { COLORS, TYPOGRAPHY_SIZES } from 'theme';
import { FilterWrapper } from '../SegmentationLabels/styled';
import { AdvancedFiltersOptions, AdvancedFilterValue } from 'models/advancedFilters';
import AdvancedFilters from '../molecules/advancedFilters';
import ActiveFilters from '../molecules/activeFilters';
import SegmentationPredictionActiveFilters from '../molecules/segmentationPredictionActiveFilters';
import {
  useFilterState,
  useKeypointFilterState,
  useSegmentationPredictionFilterState,
  usePredictionFilterState,
} from 'hooks/useFilterState';

interface IProps {
  primaryOptions: AdvancedFilterValue[];
  secondaryOptions: AdvancedFilterValue[];
  advancedFiltersOptions: AdvancedFiltersOptions;
  handlePrimaryOptChange: (e: AdvancedFilterValue) => void;
  onHandleClear: () => void;
  onHandleApply: () => void;
  isLoadingSegmentationClasses?: boolean;
  isSegmentationClassesError?: boolean;
  clearSegmentations?: () => void;
  keypointMode?: boolean;
  isSegmentationPredictionMode?: boolean;
  isPredictionMode?: boolean;
}

const Filters: FC<IProps> = props => {
  const {
    primaryOptions,
    secondaryOptions,
    advancedFiltersOptions,
    handlePrimaryOptChange,
    onHandleClear,
    onHandleApply,
    isLoadingSegmentationClasses = false,
    isSegmentationClassesError = false,
    clearSegmentations,
    keypointMode = false,
    isSegmentationPredictionMode = false,
    isPredictionMode = false,
  } = props;

  const seg = useFilterState();
  const kp = useKeypointFilterState();
  const segPred = useSegmentationPredictionFilterState(); // Reintroducing instance
  const pred = usePredictionFilterState(); // New instance for Predictions
  const { filterState, updateFilterTags } = isPredictionMode
    ? pred
    : isSegmentationPredictionMode
      ? segPred
      : keypointMode
        ? kp
        : seg;

  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingRemove, setPendingRemove] = useState<AdvancedFilterValue[]>([]);
  const [pendingApply, setPendingApply] = useState(false);
  const [pendingAdvancedFilters, setPendingAdvancedFilters] = useState(
    () => filterState.advancedFilters
  );
  const [pendingView, setPendingView] = useState(() => filterState.view);
  const [pendingFeatures, setPendingFeatures] = useState<AdvancedFilterValue[]>(
    () => filterState.features
  );
  const [pendingIncludeAllSegmentationLabels, setPendingIncludeAllSegmentationLabels] = useState(
    () => seg.filterState.includeAllSegmentationLabels
  );
  const initializedFeaturesFromUrlRef = useRef(false);

  useEffect(() => {
    setPendingView(filterState.view);
  }, [filterState.view]);

  useEffect(() => {
    setPendingAdvancedFilters(filterState.advancedFilters);
  }, [filterState.advancedFilters]);

  useEffect(() => {
    if (!isSegmentationPredictionMode && !keypointMode) {
      setPendingIncludeAllSegmentationLabels(seg.filterState.includeAllSegmentationLabels);
    }
  }, [seg.filterState.includeAllSegmentationLabels, isSegmentationPredictionMode, keypointMode]);

  const updateQueryParams = () => {
    const params: Record<string, string> = {};

    if (filterState.view?.name) {
      params['view'] = filterState.view.name;
    }
    if (filterState.features.length) {
      params['features'] = filterState.features.map((f: AdvancedFilterValue) => f.name).join(',');
    }
    if (
      !isSegmentationPredictionMode &&
      !keypointMode &&
      !seg.filterState.includeAllSegmentationLabels
    ) {
      params['include_all_segmentation_labels'] = 'false';
    }

    // Handle datasource (single select)
    if (filterState.advancedFilters.datasources && filterState.advancedFilters.datasources.name) {
      params['datasources'] = filterState.advancedFilters.datasources.name;
    }

    // Handle dicomICID (string)
    if (filterState.advancedFilters.dicomICID && filterState.advancedFilters.dicomICID.trim()) {
      params['dicomICID'] = filterState.advancedFilters.dicomICID;
    }

    // Handle study (string)
    if (filterState.advancedFilters.study && filterState.advancedFilters.study.trim()) {
      params['study'] = filterState.advancedFilters.study;
    }

    if (
      filterState.advancedFilters.segmentationICID &&
      filterState.advancedFilters.segmentationICID.trim()
    ) {
      params['segmentationICID'] = filterState.advancedFilters.segmentationICID;
    }

    if (
      filterState.advancedFilters.keypointICID &&
      filterState.advancedFilters.keypointICID.trim()
    ) {
      params['keypointICID'] = filterState.advancedFilters.keypointICID;
    }

    if (filterState.advancedFilters.technician && filterState.advancedFilters.technician.trim()) {
      params['technician'] = filterState.advancedFilters.technician;
    }

    if (
      isSegmentationPredictionMode &&
      filterState.advancedFilters.source_name &&
      typeof filterState.advancedFilters.source_name === 'string' &&
      filterState.advancedFilters.source_name.trim()
    ) {
      params['source_name'] = filterState.advancedFilters.source_name;
    }

    // Annotation set name (for segmentation labels, keypoints, and view labels — not for segmentation predictions)
    if (
      !isSegmentationPredictionMode &&
      filterState.advancedFilters.annotationSetName &&
      typeof filterState.advancedFilters.annotationSetName === 'string' &&
      filterState.advancedFilters.annotationSetName.trim()
    ) {
      params['annotation_set_name'] = filterState.advancedFilters.annotationSetName;
    }

    // (Do not emit the short `annotation_set` alias here to avoid duplicate
    // query params when Apply is used; individual pages may read the short
    // alias from the URL for compatibility.)

    if (filterState.advancedFilters.created_at_after) {
      params['created_at_after'] = filterState.advancedFilters.created_at_after;
    }
    if (filterState.advancedFilters.created_at_before) {
      params['created_at_before'] = filterState.advancedFilters.created_at_before;
    }
    if (filterState.advancedFilters.updated_at_after) {
      params['updated_at_after'] = filterState.advancedFilters.updated_at_after;
    }
    if (filterState.advancedFilters.updated_at_before) {
      params['updated_at_before'] = filterState.advancedFilters.updated_at_before;
    }

    setSearchParams(params);
  };

  useEffect(() => {
    if (!primaryOptions.length) return;

    const viewParam = searchParams.get('view');
    if (viewParam) {
      const foundView =
        primaryOptions.find(opt => opt.name === viewParam) ||
        primaryOptions.find(opt => opt.uuid === viewParam);
      if (foundView) {
        if (isSegmentationPredictionMode) {
          segPred.setView(foundView);
        } else if (keypointMode) {
          kp.setView(foundView);
        } else {
          seg.setView(foundView);
        }
        setPendingView(foundView);
      } else {
        if (isSegmentationPredictionMode) {
          segPred.setView(undefined);
        } else if (keypointMode) {
          kp.setView(undefined);
        } else {
          seg.setView(undefined);
        }
        setPendingView(undefined);
      }
    }
  }, [searchParams, primaryOptions, isSegmentationPredictionMode, keypointMode]); // Reintroducing isSegmentationPredictionMode

  useEffect(() => {
    if (!secondaryOptions.length) return;
    const features = searchParams.get('features');
    if (features) {
      const featureParams = features
        .split(',')
        .map(f => decodeURIComponent(f))
        .map(f => f.replace(/\+/g, ' '));
      const selectedFeatures = secondaryOptions.filter(
        opt => featureParams.includes(opt.name) || featureParams.includes(opt.uuid)
      );
      setPendingFeatures(selectedFeatures);
      // Auto-apply features from URL only once on initial load
      if (!initializedFeaturesFromUrlRef.current) {
        if (isSegmentationPredictionMode) {
          segPred.setFeatures(selectedFeatures);
        } else if (keypointMode) {
          kp.setFeatures(selectedFeatures);
        } else {
          seg.setFeatures(selectedFeatures);
        }
        initializedFeaturesFromUrlRef.current = true;
      }
    } else {
      setPendingFeatures([]);
      if (isSegmentationPredictionMode) {
        segPred.setFeatures([]);
      } else if (keypointMode) {
        kp.setFeatures([]);
      } else {
        seg.setFeatures([]);
      }
    }
  }, [searchParams, secondaryOptions, isSegmentationPredictionMode, keypointMode]); // Reintroducing isSegmentationPredictionMode

  useEffect(() => {
    if (isSegmentationPredictionMode || keypointMode) return;

    const includeAll = searchParams.get('include_all_segmentation_labels') !== 'false';
    setPendingIncludeAllSegmentationLabels(includeAll);
    seg.setIncludeAllSegmentationLabels(includeAll);
  }, [searchParams, isSegmentationPredictionMode, keypointMode]);

  useEffect(() => {
    // --- Datasource: map name or uuid to object (single-select) ---
    const datasourceParam = searchParams.get('datasources');
    if (datasourceParam && advancedFiltersOptions.datasources) {
      const foundDatasource = (advancedFiltersOptions.datasources as AdvancedFilterValue[]).find(
        opt => opt.name === datasourceParam || opt.uuid === datasourceParam
      );
      if (isSegmentationPredictionMode) {
        segPred.setAdvancedFilter('datasources', foundDatasource || undefined);
      } else if (keypointMode) {
        kp.setAdvancedFilter('datasources', foundDatasource || undefined);
      } else {
        seg.setAdvancedFilter('datasources', foundDatasource || undefined);
      }
    } else {
      if (isSegmentationPredictionMode) {
        segPred.setAdvancedFilter('datasources', undefined);
      } else if (keypointMode) {
        kp.setAdvancedFilter('datasources', undefined);
      } else {
        seg.setAdvancedFilter('datasources', undefined);
      }
    }

    // --- DicomICID: string ---
    const dicomICIDParam = searchParams.get('dicomICID');
    if (dicomICIDParam) {
      if (isSegmentationPredictionMode) {
        segPred.setAdvancedFilter('dicomICID', dicomICIDParam);
      } else if (keypointMode) {
        kp.setAdvancedFilter('dicomICID', dicomICIDParam);
      } else {
        seg.setAdvancedFilter('dicomICID', dicomICIDParam);
      }
    } else {
      if (isSegmentationPredictionMode) {
        segPred.setAdvancedFilter('dicomICID', '');
      } else if (keypointMode) {
        kp.setAdvancedFilter('dicomICID', '');
      } else {
        seg.setAdvancedFilter('dicomICID', '');
      }
    }

    // --- Study: string ---
    const studyParam = searchParams.get('study');
    if (isSegmentationPredictionMode) {
      segPred.setAdvancedFilter('study', studyParam || '');
    } else if (keypointMode) {
      kp.setAdvancedFilter('study', studyParam || '');
    } else {
      seg.setAdvancedFilter('study', studyParam || '');
    }

    const segmentationICIDParam = searchParams.get('segmentationICID');
    if (segmentationICIDParam) {
      if (isSegmentationPredictionMode) {
        segPred.setAdvancedFilter('segmentationICID', segmentationICIDParam);
      } else if (keypointMode) {
        kp.setAdvancedFilter('segmentationICID', segmentationICIDParam);
      } else {
        seg.setAdvancedFilter('segmentationICID', segmentationICIDParam);
      }
    } else {
      if (isSegmentationPredictionMode) {
        segPred.setAdvancedFilter('segmentationICID', '');
      } else if (keypointMode) {
        kp.setAdvancedFilter('segmentationICID', '');
      } else {
        seg.setAdvancedFilter('segmentationICID', '');
      }
    }
    // --- Frame ID(s): support comma-separated list or single value ---
    const frameIdParam = searchParams.get('frame_id');
    if (frameIdParam) {
      const frameVal = decodeURIComponent(frameIdParam);
      if (isSegmentationPredictionMode) {
        segPred.setAdvancedFilter('frameId', frameVal);
      } else if (keypointMode) {
        kp.setAdvancedFilter('frameId', frameVal);
      } else {
        seg.setAdvancedFilter('frameId', frameVal);
      }
    } else {
      if (isSegmentationPredictionMode) segPred.setAdvancedFilter('frameId', '');
      else if (keypointMode) kp.setAdvancedFilter('frameId', '');
      else seg.setAdvancedFilter('frameId', '');
    }
    // --- Review status: comma-separated list -> array of statuses ---
    const reviewStatusParam = searchParams.get('review_status');
    if (reviewStatusParam) {
      const statuses = decodeURIComponent(reviewStatusParam)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      if (isSegmentationPredictionMode) {
        segPred.setAdvancedFilter('reviewStatus', statuses);
      } else if (keypointMode) {
        kp.setAdvancedFilter('reviewStatus', statuses);
      } else {
        seg.setAdvancedFilter('reviewStatus', statuses);
      }
    } else {
      if (isSegmentationPredictionMode) segPred.setAdvancedFilter('reviewStatus', []);
      else if (keypointMode) kp.setAdvancedFilter('reviewStatus', []);
      else seg.setAdvancedFilter('reviewStatus', []);
    }

    const keypointICIDParam = searchParams.get('keypointICID');
    if (keypointICIDParam) {
      if (isSegmentationPredictionMode) {
        segPred.setAdvancedFilter('keypointICID', keypointICIDParam);
      } else if (keypointMode) {
        kp.setAdvancedFilter('keypointICID', keypointICIDParam);
      } else {
        seg.setAdvancedFilter('keypointICID', keypointICIDParam);
      }
    } else {
      if (isSegmentationPredictionMode) {
        segPred.setAdvancedFilter('keypointICID', '');
      } else if (keypointMode) {
        kp.setAdvancedFilter('keypointICID', '');
      } else {
        seg.setAdvancedFilter('keypointICID', '');
      }
    }

    const technicianParam = searchParams.get('technician');
    if (technicianParam) {
      if (isSegmentationPredictionMode) {
        segPred.setAdvancedFilter('technician', technicianParam);
      } else if (keypointMode) {
        kp.setAdvancedFilter('technician', technicianParam);
      } else {
        seg.setAdvancedFilter('technician', technicianParam);
      }
    } else {
      if (isSegmentationPredictionMode) {
        segPred.setAdvancedFilter('technician', '');
      } else if (keypointMode) {
        kp.setAdvancedFilter('technician', '');
      } else {
        seg.setAdvancedFilter('technician', '');
      }
    }

    const sourceNameParam = searchParams.get('source_name');
    if (isSegmentationPredictionMode) {
      segPred.setAdvancedFilter('source_name', sourceNameParam || '');
    } else if (keypointMode) {
      kp.setAdvancedFilter('source_name', sourceNameParam || '');
    } else {
      seg.setAdvancedFilter('source_name', sourceNameParam || '');
    }

    const annotationSetParam = searchParams.get('annotation_set_name');
    if (!isSegmentationPredictionMode && annotationSetParam) {
      if (keypointMode) {
        kp.setAdvancedFilter('annotationSetName', annotationSetParam || '');
      } else {
        // default to segmentation/view slice
        seg.setAdvancedFilter('annotationSetName', annotationSetParam || '');
      }
    } else {
      // clear when absent
      if (keypointMode) kp.setAdvancedFilter('annotationSetName', '');
      else seg.setAdvancedFilter('annotationSetName', '');
    }

    // Support short `annotation_set` param as backward-compatible alias
    const annotationSetShort = searchParams.get('annotation_set');
    if (!isSegmentationPredictionMode && annotationSetShort) {
      if (keypointMode) kp.setAdvancedFilter('annotationSetName', annotationSetShort || '');
      else seg.setAdvancedFilter('annotationSetName', annotationSetShort || '');
    }

    const createdAtAfterParam = searchParams.get('created_at_after');
    if (isSegmentationPredictionMode) {
      segPred.setAdvancedFilter('created_at_after', createdAtAfterParam || undefined);
    } else if (keypointMode) {
      kp.setAdvancedFilter('created_at_after', createdAtAfterParam || undefined);
    } else {
      seg.setAdvancedFilter('created_at_after', createdAtAfterParam || undefined);
    }

    const createdAtBeforeParam = searchParams.get('created_at_before');
    if (isSegmentationPredictionMode) {
      segPred.setAdvancedFilter('created_at_before', createdAtBeforeParam || undefined);
    } else if (keypointMode) {
      kp.setAdvancedFilter('created_at_before', createdAtBeforeParam || undefined);
    } else {
      seg.setAdvancedFilter('created_at_before', createdAtBeforeParam || undefined);
    }

    const updatedAtAfterParam = searchParams.get('updated_at_after');
    if (isSegmentationPredictionMode) {
      segPred.setAdvancedFilter('updated_at_after', updatedAtAfterParam || undefined);
    } else if (keypointMode) {
      kp.setAdvancedFilter('updated_at_after', updatedAtAfterParam || undefined);
    } else {
      seg.setAdvancedFilter('updated_at_after', updatedAtAfterParam || undefined);
    }

    const updatedAtBeforeParam = searchParams.get('updated_at_before');
    if (isSegmentationPredictionMode) {
      segPred.setAdvancedFilter('updated_at_before', updatedAtBeforeParam || undefined);
    } else if (keypointMode) {
      kp.setAdvancedFilter('updated_at_before', updatedAtBeforeParam || undefined);
    } else {
      seg.setAdvancedFilter('updated_at_before', updatedAtBeforeParam || undefined);
    }
  }, [
    searchParams,
    primaryOptions,
    advancedFiltersOptions,
    isSegmentationPredictionMode,
    keypointMode,
  ]); // Reintroducing isSegmentationPredictionMode

  useEffect(() => {
    // Only run if there are query params or filterState has actual values set
    // This useEffect is to trigger updateFilterTags, which then prepares tags for display
    const hasActiveFilters =
      filterState.view ||
      filterState.features.length > 0 ||
      Object.values(filterState.advancedFilters).some(
        val =>
          (Array.isArray(val) && val.length > 0) ||
          (typeof val === 'object' && val !== null && 'name' in val) ||
          (typeof val === 'string' && val.trim() !== '')
      );

    if (hasActiveFilters || Array.from(searchParams.keys()).length > 0) {
      updateFilterTags();
    }
  }, [
    filterState.view,
    filterState.features,
    filterState.advancedFilters,
    searchParams,
    isSegmentationPredictionMode,
    keypointMode,
  ]); // Reintroducing isSegmentationPredictionMode

  const handlePrimaryChange = (_: string, option: any) => {
    const { uuid, name } = option;
    setPendingView({ uuid, name });
    handlePrimaryOptChange({ uuid, name });
  };

  const handleSecondaryChange = (_: string[], options: any) => {
    const selectedFeatures: AdvancedFilterValue[] = options.map(
      (option: { uuid: any; name: any }) => ({
        uuid: option.uuid,
        name: option.name,
      })
    );
    setPendingFeatures(selectedFeatures);
  };

  const handleAdvancedFiltersChange = (
    key: string,
    value: AdvancedFilterValue | AdvancedFilterValue[] | string | string[] | undefined
  ) => {
    setPendingAdvancedFilters((prev: { [x: string]: any }) => ({ ...prev, [key]: value }));
  };

  const handleClear = () => {
    if (isSegmentationPredictionMode) {
      segPred.resetFilters();
    } else if (keypointMode) {
      kp.resetFilters();
    } else {
      seg.resetFilters();
    }
    onHandleClear();
    setSearchParams({});
    setPendingView(undefined);
    setPendingFeatures([]);
    setPendingAdvancedFilters({
      datasources: undefined,
      dicomICID: undefined,
      study: undefined,
      segmentationICID: undefined,
      keypointICID: undefined,
      technician: isSegmentationPredictionMode ? undefined : [],
      source_name: [], // Always initialize as an empty array
      created_at_after: undefined,
      created_at_before: undefined,
      updated_at_after: undefined,
      updated_at_before: undefined,
    });
    setPendingIncludeAllSegmentationLabels(true);
    if (clearSegmentations) clearSegmentations();
  };
  const handlePendingRemovals = () => {
    pendingRemove.forEach(tag => {
      // Remove from the actual Redux filter state
      if (isSegmentationPredictionMode) {
        segPred.removeTag(tag, false);
      } else if (keypointMode) {
        kp.removeTag(tag, false);
      } else {
        seg.removeTag(tag, false);
      }

      // Also clear any pending selections so Apply won't reintroduce them
      const tagName = tag.name || '';
      const [rawKey] = tagName.split(':');
      const key = rawKey?.trim();

      if (key?.toLowerCase() === 'view') {
        setPendingView(undefined);
      } else if (key?.toLowerCase() === 'feature') {
        // Keep pendingFeatures in sync when a Feature tag is removed
        setPendingFeatures(prev => prev.filter(f => f.uuid !== tag.uuid));
      } else if (key) {
        setPendingAdvancedFilters((prev: { [x: string]: any }) => {
          const next = { ...prev } as any;
          const currentVal = (next as any)[key];

          if (Array.isArray(currentVal)) {
            const isStringArray = currentVal.length === 0 || typeof currentVal[0] === 'string';
            if (isStringArray) {
              (next as any)[key] = currentVal.filter((item: any) => {
                const itemUuid = `${key}_${item}`;
                return itemUuid !== tag.uuid && item !== tag.uuid;
              });
            } else {
              (next as any)[key] = currentVal.filter((item: any) => item?.uuid !== tag.uuid);
            }
          } else if (currentVal && typeof currentVal === 'object') {
            if (currentVal.uuid === tag.uuid) {
              (next as any)[key] = undefined; // Changed from empty object to undefined
            }
          } else if (typeof currentVal === 'string') {
            (next as any)[key] = undefined; // Changed from empty string to undefined
          }
          return next;
        });
      }
    });
    setPendingRemove([]);
  };

  const handleTagPendingRemove = (tag: AdvancedFilterValue) => {
    const tagName = tag.name || '';
    const [rawKey] = tagName.split(':');
    const key = rawKey?.trim();

    if (key?.toLowerCase() === 'view') {
      setPendingView(undefined);
    } else if (key?.toLowerCase() === 'feature') {
      setPendingFeatures(prev => prev.filter(f => f.uuid !== tag.uuid));
    } else if (key) {
      setPendingAdvancedFilters((prev: { [x: string]: any }) => {
        const next = { ...prev } as any;
        const currentVal = (next as any)[key];

        if (Array.isArray(currentVal)) {
          const isStringArray = currentVal.length === 0 || typeof currentVal[0] === 'string';
          if (isStringArray) {
            (next as any)[key] = currentVal.filter((item: any) => {
              const itemUuid = `${key}_${item}`;
              return itemUuid !== tag.uuid && item !== tag.uuid;
            });
          } else {
            (next as any)[key] = currentVal.filter((item: any) => item?.uuid !== tag.uuid);
          }
        } else if (currentVal && typeof currentVal === 'object') {
          if (currentVal.uuid === tag.uuid) {
            (next as any)[key] = undefined; // Changed from empty object to undefined
          }
        } else if (typeof currentVal === 'string') {
          (next as any)[key] = undefined; // Changed from empty string to undefined
        }
        return next;
      });
    }
    // remove matching URL param immediately so UI/URL stay in sync
    try {
      const paramMap: { [k: string]: string } = {
        view: 'view',
        features: 'features',
        datasources: 'datasources',
        dicomicid: 'dicomICID',
        study: 'study',
        segmentationicid: 'segmentationICID',
        keypointicid: 'keypointICID',
        viewlabelicid: 'viewLabelICID',
        technician: 'technician',
        source_name: 'source_name',
        annotationsetname: 'annotation_set_name',
        created_at_after: 'created_at_after',
        created_at_before: 'created_at_before',
        updated_at_after: 'updated_at_after',
        updated_at_before: 'updated_at_before',
        frameid: 'frame_id',
        reviewstatus: 'review_status',
      };

      const rawKeyLower = (rawKey || '').trim().toLowerCase();
      const paramName = paramMap[rawKeyLower];
      if (paramName) {
        const val = tagName.split(':').slice(1).join(':').trim();
        const newParams = new URLSearchParams(searchParams);
        const existing = newParams.get(paramName);
        if (existing) {
          const partsRaw = existing.split(',');
          const parts = partsRaw.map(p => decodeURIComponent(p).trim()).filter(Boolean);
          // Support tags that represent multiple comma-separated values (e.g. dicomICID lists)
          const decodedVal = decodeURIComponent(val);
          let filtered: string[] = [];
          if (decodedVal.includes(',')) {
            const removePartsRaw = decodedVal.split(',');
            const removeParts = removePartsRaw.map(p => p.trim()).filter(Boolean);
            filtered = parts.filter(p => !removeParts.includes(p));
          } else {
            filtered = parts.filter(p => p !== decodedVal);
          }
          if (filtered.length === 0) {
            newParams.delete(paramName);
          } else {
            newParams.set(paramName, filtered.join(','));
          }
        }
        setSearchParams(newParams);
      }
    } catch (err) {
      // ignore URL update errors
    }
  };
  const handleApply = () => {
    handlePendingRemovals();
    if (isSegmentationPredictionMode) {
      if (pendingView) segPred.setView(pendingView);
      else segPred.setView(undefined);
      segPred.setFeatures(pendingFeatures);
      (Object.entries(pendingAdvancedFilters) as [string, any][]).forEach(([key, value]) => {
        segPred.setAdvancedFilter(key, value as any);
      });
    } else if (keypointMode) {
      if (pendingView) kp.setView(pendingView);
      else kp.setView(undefined);
      kp.setFeatures(pendingFeatures);
      (Object.entries(pendingAdvancedFilters) as [string, any][]).forEach(([key, value]) => {
        kp.setAdvancedFilter(key, value as any);
      });
    } else {
      if (pendingView) seg.setView(pendingView);
      else seg.setView(undefined);
      seg.setFeatures(pendingFeatures);
      seg.setIncludeAllSegmentationLabels(pendingIncludeAllSegmentationLabels);
      (Object.entries(pendingAdvancedFilters) as [string, any][]).forEach(([key, value]) => {
        seg.setAdvancedFilter(key, value as any);
      });
    }
    // Update URL immediately from pending values so query param appears even before Redux state sync
    const paramsNow: Record<string, string> = {};
    if (pendingView?.name) paramsNow['view'] = pendingView.name;
    if (pendingFeatures.length)
      paramsNow['features'] = pendingFeatures.map((f: AdvancedFilterValue) => f.name).join(',');

    const af = pendingAdvancedFilters as any;
    if (!isSegmentationPredictionMode && !keypointMode && !pendingIncludeAllSegmentationLabels)
      paramsNow['include_all_segmentation_labels'] = 'false';
    if (af?.datasources && af.datasources.name) paramsNow['datasources'] = af.datasources.name;
    if (af?.dicomICID) paramsNow['dicomICID'] = af.dicomICID;
    if (af?.study) paramsNow['study'] = af.study;
    if (af?.segmentationICID) paramsNow['segmentationICID'] = af.segmentationICID;
    if (af?.keypointICID) paramsNow['keypointICID'] = af.keypointICID;
    if (af?.technician) paramsNow['technician'] = af.technician;
    if (isSegmentationPredictionMode && af?.source_name) paramsNow['source_name'] = af.source_name;
    if (!isSegmentationPredictionMode && af?.annotationSetName)
      paramsNow['annotation_set_name'] = af.annotationSetName;
    if (af?.created_at_after) paramsNow['created_at_after'] = af.created_at_after;
    if (af?.created_at_before) paramsNow['created_at_before'] = af.created_at_before;
    if (af?.updated_at_after) paramsNow['updated_at_after'] = af.updated_at_after;
    if (af?.updated_at_before) paramsNow['updated_at_before'] = af.updated_at_before;
    // Frame ID and review status (added support)
    if (af?.frameId) paramsNow['frame_id'] = af.frameId;
    if (af?.reviewStatus && Array.isArray(af.reviewStatus) && af.reviewStatus.length > 0)
      paramsNow['review_status'] = af.reviewStatus.join(',');

    setSearchParams(paramsNow);

    setPendingApply(true);
  };

  const isObjectFilterEqual = (a: any, b: any) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a.uuid === b.uuid && a.name === b.name;
  };

  const areFeaturesEqual = (a: AdvancedFilterValue[], b: AdvancedFilterValue[]) => {
    if (a.length !== b.length) return false;
    const aIds = a.map(f => f.uuid).sort();
    const bIds = b.map(f => f.uuid).sort();
    return aIds.every((id, idx) => id === bIds[idx]);
  };

  const areAdvancedFiltersEqual = (a: any, b: any) => {
    const keys = [
      'datasources',
      'dicomICID',
      'study',
      'segmentationICID',
      'annotationSetName',
      'keypointICID',
      'technician',
      'source_name',
      'created_at_after',
      'created_at_before',
      'updated_at_after',
      'updated_at_before',
      'frameId',
      'reviewStatus',
    ];
    for (const key of keys) {
      const av = a?.[key];
      const bv = b?.[key];
      if (typeof av === 'string' || typeof bv === 'string') {
        if ((av || '') !== (bv || '')) return false;
      } else {
        if (Array.isArray(av) || Array.isArray(bv)) {
          if (!areFeaturesEqual(av || [], bv || [])) return false;
        } else if (!isObjectFilterEqual(av, bv)) return false;
      }
    }
    return true;
  };

  const isFilterStateSyncedWithPending = () => {
    const syncedView = isObjectFilterEqual(filterState.view, pendingView);
    const syncedFeatures = areFeaturesEqual(filterState.features, pendingFeatures);
    const syncedAdv = areAdvancedFiltersEqual(filterState.advancedFilters, pendingAdvancedFilters);
    const syncedIncludeAll =
      isSegmentationPredictionMode || keypointMode
        ? true
        : seg.filterState.includeAllSegmentationLabels === pendingIncludeAllSegmentationLabels;
    return syncedView && syncedFeatures && syncedAdv && syncedIncludeAll;
  };

  useEffect(() => {
    if (!pendingApply) return;
    if (!isFilterStateSyncedWithPending()) return;
    updateQueryParams();
    updateFilterTags();
    onHandleApply();
    setPendingApply(false);
  }, [filterState, pendingApply]);

  const featureOptions = secondaryOptions;

  return (
    <FilterWrapper>
      <Card>
        <Flex vertical gap="middle">
          <Row gutter={[16, 16]} style={{ width: '100%' }} align="bottom">
            <Col xs={24} md={12} xl={6}>
              <Flex vertical>
                <Text size={TYPOGRAPHY_SIZES.NORMAL} color={COLORS.darkBlue} weight={600}>
                  View
                </Text>
                <Select
                  showSearch
                  placeholder="Choose a view"
                  optionFilterProp="label"
                  onChange={handlePrimaryChange}
                  fieldNames={{
                    label: 'name',
                    value: 'uuid',
                  }}
                  filterOption={(input, option) =>
                    (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={primaryOptions}
                  value={pendingView?.uuid}
                />
              </Flex>
            </Col>
            <Col xs={24} md={12} xl={isSegmentationPredictionMode || keypointMode ? 14 : 7}>
              <Flex vertical>
                <Text size={TYPOGRAPHY_SIZES.NORMAL} color={COLORS.darkBlue} weight={602}>
                  Feature (union)
                </Text>
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  mode="multiple"
                  value={pendingFeatures.map((feature: AdvancedFilterValue) => feature.uuid)}
                  allowClear
                  style={{ width: '100%' }}
                  fieldNames={{
                    label: 'name',
                    value: 'uuid',
                  }}
                  placeholder={
                    isLoadingSegmentationClasses ? 'Loading features...' : 'Choose features'
                  }
                  options={featureOptions}
                  onChange={handleSecondaryChange}
                  loading={isLoadingSegmentationClasses}
                  disabled={isLoadingSegmentationClasses || isSegmentationClassesError}
                  dropdownRender={menu =>
                    isSegmentationClassesError ? (
                      <div style={{ padding: '12px', textAlign: 'center' }}>
                        <Typography.Text type="danger">
                          Failed to load features. Please try selecting a view again.
                        </Typography.Text>
                      </div>
                    ) : (
                      <>{menu}</>
                    )
                  }
                  notFoundContent={
                    isLoadingSegmentationClasses ? (
                      'Loading features...'
                    ) : isSegmentationClassesError ? (
                      <Typography.Text type="danger">Failed to load features</Typography.Text>
                    ) : (
                      'No features found'
                    )
                  }
                />
              </Flex>
            </Col>
            {!isSegmentationPredictionMode && !keypointMode && (
              <Col xs={24} md={12} xl={3}>
                <Flex vertical align="flex-start">
                  <Text size={TYPOGRAPHY_SIZES.NORMAL} color={COLORS.darkBlue} weight={602}>
                    Show all labels in frame
                  </Text>
                  <ConfigProvider
                    theme={{
                      components: {
                        Switch: {
                          trackHeight: 32,
                          trackMinWidth: 104,
                          handleSize: 28,
                          innerMinMargin: 4,
                          innerMaxMargin: 34,
                        },
                      },
                    }}
                  >
                    <Switch
                      checked={pendingIncludeAllSegmentationLabels}
                      checkedChildren="All labels"
                      unCheckedChildren="Matches only"
                      onChange={setPendingIncludeAllSegmentationLabels}
                    />
                  </ConfigProvider>
                </Flex>
              </Col>
            )}
            <Col xs={24} md={24} xl={4}>
              <AdvancedFilters
                advancedFiltersOptions={advancedFiltersOptions}
                handleAdvancedFiltersOptChange={handleAdvancedFiltersChange}
                advancedFilters={pendingAdvancedFilters as any}
                isKeypointMode={keypointMode}
                isSegmentationPredictionMode={isSegmentationPredictionMode}
              />
            </Col>
          </Row>

          {isSegmentationPredictionMode ? (
            <SegmentationPredictionActiveFilters
              pendingRemove={pendingRemove}
              setPendingRemove={setPendingRemove}
              onTagPendingRemove={handleTagPendingRemove}
            />
          ) : (
            <ActiveFilters
              pendingRemove={pendingRemove}
              setPendingRemove={setPendingRemove}
              keypointMode={keypointMode}
              onTagPendingRemove={handleTagPendingRemove}
            />
          )}

          <Flex gap={8} justify="end">
            <Button onClick={handleClear}>Clear</Button>
            <Button onClick={handleApply} type="primary">
              Apply Filters
            </Button>
          </Flex>
        </Flex>
      </Card>
    </FilterWrapper>
  );
};

export default Filters;
